// Info-Reiter in einfachem JavaScript (ohne React, ohne Build-Werkzeug nötig): Probe für die Vorlage der WohnBau-Apps.
// Erzeugt dasselbe Markup wie `InfoTab.tsx`; ein Test (`plain/info.test.tsx`) vergleicht beide. Zustand und Aktionen siehe `InfoState` und `InfoActions` in `InfoTab.tsx`; Ereignisse über data-Attribute (siehe `kit.js`).

import { h } from './kit.js';

function button(label, action, className = 'wb-secondary', attributes = {}) {
  return h('button', { ...attributes, class: className, 'data-click': action }, label);
}

function logLine(entry) {
  return `${new Date(entry.at).toLocaleString('de-DE')}: ${entry.event}${entry.detail ? ` (${entry.detail})` : ''}`;
}

/** Hinweis zur Erfassung ohne Netz, dort wo es um die installierte App geht (statt eines eigenen Abschnitts „Funktionen“). */
function offlineHint() {
  return h('p', { class: 'wb-help hint' }, 'Ohne Netz geht die Erfassung nur in der installierten App (PWA). Dort mit „Jetzt synchronisieren“ übertragen; oben rechts zeigt ein Status, ob die App online oder offline ist und ob die Offline-Erfassung bereit ist.');
}

function aboutSection(s) {
  const standalone = s.mode === 'pwa' || s.mode === 'browser';
  const rows = [
    ['Anwendung', s.appName], ['Herausgeber', 'WohnBau Westmünsterland eG'],
    ['App-Version', s.appVersion, true], ['Azure-Function-Version', s.functionVersion ?? '–', true],
    ['Teams-App-Paket', s.manifestVersion, true], ['Baseline-Version', s.baselineVersion, true],
    ['Angemeldet als', s.userName ?? '–'], ['Ihre App-Rollen', s.roles && s.roles.length ? s.roles.join(', ') : 'Benutzer'],
    ['Umgebung', s.modeLabel],
    ['Datenquelle', s.dataSource ? `SharePoint-Liste „${s.dataSource.list}“, Bibliothek „${s.dataSource.library}“` : 'SharePoint'],
    ['Verbindung', s.online ? 'Online' : 'Offline']
  ];
  const section = h('section', { class: 'wb-form-section spaced', style: 'order:1' },
    h('h2', {}, 'Über die App'),
    h('dl', { class: 'wb-info-grid' }, rows.map(([label, value, short]) => [h('dt', {}, label), h('dd', { class: short ? 'short' : false }, value)])));
  if (standalone) {
    const content = h('div', { class: 'wb-disclosure-content' },
      h('p', { class: 'wb-help hint' }, 'Die letzten Anmelde-Ereignisse auf diesem Gerät, zur Fehlersuche bei erneuten Anmeldungen. Es stehen nur Zeit, Ereignis und Fehlercode darin, keine Namen und keine Token.'));
    if (s.authLog.length === 0) content.append(h('p', { class: 'wb-help hint' }, 'Noch keine Einträge.'));
    else {
      content.append(h('ul', {}, s.authLog.slice().reverse().map(entry => h('li', {}, logLine(entry)))));
      content.append(h('div', { class: 'wb-actions' }, button('Protokoll kopieren', 'copyAuthLog', 'wb-secondary', { type: 'button' })));
      if (s.authLogCopied) content.append(h('p', { class: 'wb-help hint', role: 'status' }, s.authLogCopied));
    }
    section.append(h('details', { class: 'wb-disclosure' }, h('summary', {}, 'Anmeldeprotokoll (dieses Gerät)'), content));
    section.append(offlineHint(), h('div', { class: 'wb-actions' },
      s.mode === 'browser' && button('App installieren', 'installPwa'),
      button('Abmelden', 'signOut')));
  } else if (s.embedded) {
    section.append(offlineHint(), h('div', { class: 'wb-actions' }, button('PWA im Browser öffnen', 'openPwa'), button('Link kopieren', 'copyLink')));
  }
  return section;
}

function historySection(s) {
  if (s.releaseNotes.length === 0) return null;
  const section = h('section', { class: 'wb-form-section spaced', style: 'order:2' }, h('h2', {}, 'Versionsverlauf'));
  for (const note of s.releaseNotes.slice(0, s.showAllVersions ? undefined : 10)) {
    const installed = note.version === s.appVersion;
    section.append(h('details', { class: 'wb-disclosure', open: installed },
      h('summary', {}, `Version ${note.version}`, installed && h('span', { class: 'wb-disclosure-meta' }, 'installiert')),
      h('div', { class: 'wb-disclosure-content' }, h('ul', {}, note.items.map(item => h('li', {},
        s.isAdmin && item.text ? [h("strong", {}, `${item.heading}.`), " ", item.text] : item.heading))))));
  }
  if (!s.showAllVersions && s.releaseNotes.length > 10) section.append(h('div', { class: 'wb-actions' }, button('Ältere Versionen anzeigen', 'showAllVersions')));
  return section;
}

function legalSection() {
  return h('section', { class: 'wb-form-section spaced', style: 'order:3' }, h('h2', {}, 'Rechtliches'),
    h('div', { class: 'wb-links' },
      h('a', { href: 'https://wohnbau-wml.de/datenschutz/', target: '_blank', rel: 'noopener noreferrer' }, 'Datenschutz'),
      h('a', { href: 'https://wohnbau-wml.de/impressum/', target: '_blank', rel: 'noopener noreferrer' }, 'Impressum')));
}

/** Ansicht des Info-Reiters zu einem Zustand (`InfoState` in `InfoTab.tsx`). */
export function infoView(state) {
  return h('main', { class: 'wb-panel wb-form-grid two-columns', role: 'tabpanel', id: 'panel-info', 'aria-labelledby': 'tab-info' },
    h('div', { class: 'wb-column' }, aboutSection(state), legalSection()),
    h('div', { class: 'wb-column' }, historySection(state)));
}

/** Ordnet die Ereignisse des Markups (data-Attribute) den Aktionen der App zu (`InfoActions` in `InfoTab.tsx`). */
export function infoActions(a) {
  return {
    installPwa: () => a.installPwa(), signOut: () => a.signOut(), openPwa: () => a.openPwa(), copyLink: () => a.copyLink(),
    copyAuthLog: () => a.copyAuthLog(), showAllVersions: () => a.showAllVersions()
  };
}
